/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiEdit2, FiEye, FiTrash2, FiPlus, FiX, FiImage } from "react-icons/fi";
import PageContainer from "../components/PageContainer";
import ResponsiveTable from "../components/ResponsiveTable";
import { useUI } from "../theme/ThemeProvider";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { useTenantStorePermissions } from "../../hooks/useTenantStorePermissions";
import NoAccess from "../components/NoAccess";
import { convertImageToWebp, toWebpFileName } from "../../utils/convertImageToWebp";
// Using colors from useUI instead of hardcoded COLORS

export default function AdminBanners() {
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const perms = useTenantStorePermissions();
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/").replace(/\/+$/, '');
  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
  console.log(tenantId);

  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [showView, setShowView] = useState(null);
  const [showDelete, setShowDelete] = useState(null);
  const [newBanner, setNewBanner] = useState({
    name: "",
    active: true,
    image: "",
    imageFile: null
  });
  const [saving, setSaving] = useState(false);

  // Generate unique ID for new banners
  const generateUniqueId = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    const id = timestamp + random;
    // Ensure ID is within int32 limits and greater than 0
    return Math.max(1, Math.min(id, 2147483647));
  };

  const compressImage = (file, maxSizeKB = 400, maxWidth = 1920, maxHeight = 1080) =>
    convertImageToWebp(file, { quality: 0.8, maxWidth, maxHeight, maxSizeKB });

  // Fetch banners from API
  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    if (tenantId <= 0) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");



      const res = await fetch(`${baseUrl}/Banners/GetBanners?tenantId=${tenantId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
      });

      let data = [];
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        try {
          data = await res.json();
        } catch (parseError) {
          const text = await res.text();
          console.error("Failed to parse JSON response:", parseError);
          if (text) {
            try {
              data = JSON.parse(text);
            } catch {
              data = [];
            }
          }
        }
      } else {
        const text = await res.text();
        if (text) {
          try {
            data = JSON.parse(text);
          } catch {
            data = [];
          }
        }
      }

      if (!res.ok) {
        throw new Error(data?.message || data?.error || `HTTP ${res.status}: ${res.statusText}`);
      }

      if (Array.isArray(data)) {
        const mappedBanners = data.map((b) => ({
          id: b.id || 0,
          name: b.name || "",
          image: b.imageUrl
            ? `${baseUrl.replace("/api", "")}${b.imageUrl}`
            : b.image || "",
          active: b.active === true || b.active === 1,
        }));
        setBanners(mappedBanners);
        console.log("✅ GetBanners Response:", mappedBanners);
      } else {
        setBanners([]);
      }
    } catch (err) {
      console.error("GetBanners error:", err);
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!perms.addBanners) {
      return;
    }
    if (!newBanner.name.trim()) {
      Swal.fire({
        icon: "warning",
        text: isArabic ? "يرجى إدخال اسم اللافتة" : "Please enter banner name",
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
      return;
    }

    if (!newBanner.imageFile) {
      Swal.fire({
        icon: "warning",
        text: isArabic ? "يرجى اختيار صورة للافتة" : "Please select a banner image",
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("authToken");

      // Compress image
      const compressedFile = await compressImage(newBanner.imageFile);
      const bannerId = generateUniqueId();

      // Create FormData
      const formData = new FormData();
      formData.append("Id", bannerId.toString());
      formData.append("TenantId", tenantId.toString());
      formData.append("Name", newBanner.name.trim());
      formData.append("ImageFile", compressedFile, compressedFile.name || toWebpFileName(newBanner.imageFile.name));
      formData.append("Active", newBanner.active ? "true" : "false");

      console.log("📤 Adding banner:", {
        Id: bannerId,
        TenantId: tenantId,
        Name: newBanner.name.trim(),
        Active: newBanner.active,
      });

      const res = await fetch(`${baseUrl}/Banners/AddTenantBanner?TenantId=${tenantId}`, {
        method: "POST",
        headers: {
          "Accept": "*/*",
          "Authorization": token ? `Bearer ${token}` : "",
        },
        body: formData,
      });

      let result = {};
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        try {
          result = await res.json();
        } catch (parseError) {
          const text = await res.text();
          console.error("Failed to parse JSON response:", parseError);
          result = { message: text || `HTTP ${res.status}: ${res.statusText}` };
        }
      } else {
        const text = await res.text();
        if (text) {
          try {
            result = JSON.parse(text);
          } catch {
            result = { message: text || `HTTP ${res.status}: ${res.statusText}` };
          }
        }
      }

      console.log("📥 AddTenantBanner response status:", res.status, res.statusText);
      console.log("📥 AddTenantBanner response data:", result);

      if (!res.ok) {
        const errorMessage = result?.message || result?.error || result?.Message || "Failed to add banner";
        throw new Error(errorMessage);
      }

      Swal.fire({
        icon: "success",
        title: isArabic ? "تم الحفظ! ✅" : "Saved! ✅",
        text: result?.message || (isArabic ? "تم إضافة اللافتة بنجاح" : "Banner added successfully"),
        timer: 1500,
        showConfirmButton: false,
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });

      setNewBanner({ name: "", active: true, image: "", imageFile: null });
      setShowAdd(false);
      fetchBanners();
    } catch (err) {
      console.error("❌ Error adding banner:", err);
      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل الحفظ ❌" : "Save Failed ❌",
        text: err.message || (isArabic ? "فشل إضافة اللافتة" : "Failed to add banner"),
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEditSave = async () => {
    if (!perms.editBanners) {
      return;
    }
    if (!showEdit.name.trim()) {
      Swal.fire({
        icon: "warning",
        text: isArabic ? "يرجى إدخال اسم اللافتة" : "Please enter banner name",
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
      return;
    }

    if (!showEdit.image && !showEdit.imageFile) {
      Swal.fire({
        icon: "warning",
        text: isArabic ? "يرجى اختيار صورة للافتة" : "Please select a banner image",
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("authToken");

      const bannerId = showEdit.id;
      console.log("📤 Starting banner update process (delete-then-add) for bannerId:", bannerId);

      // Step 1: Delete the existing banner
      console.log("🗑️ Step 1: Deleting existing banner...");
      const deleteRes = await fetch(
        `${baseUrl}/Banners/DeleteBanner?id=${bannerId}&tenantId=${tenantId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "Authorization": token ? `Bearer ${token}` : "",
          },
        }
      );

      if (!deleteRes.ok) {
        const deleteError = await deleteRes.text();
        console.error("❌ Delete failed:", deleteError);
        throw new Error(isArabic ? "فشل حذف اللافتة القديمة" : "Failed to delete old banner");
      }

      console.log("✅ Step 1 complete: Banner deleted");

      // Step 2: Re-add the banner with the same ID and updated data
      console.log("➕ Step 2: Re-adding banner with updated data...");

      // Create FormData
      const formData = new FormData();
      formData.append("Id", bannerId.toString());
      formData.append("TenantId", tenantId.toString());
      formData.append("Name", showEdit.name.trim());
      formData.append("Active", showEdit.active ? "true" : "false");

      // If new image file is selected, compress and add it
      if (showEdit.imageFile) {
        console.log("📸 New image selected, compressing...");
        const compressedFile = await compressImage(showEdit.imageFile);
        formData.append("ImageFile", compressedFile, compressedFile.name || toWebpFileName(showEdit.imageFile.name));
      } else {
        // If no new image, try to use the existing image
        console.log("📸 No new image, keeping existing image");
        // We need to create a placeholder file from the existing image URL
        try {
          // Fetch the existing image and convert to blob
          const imageResponse = await fetch(showEdit.image);
          const imageBlob = await imageResponse.blob();
          const fileName = toWebpFileName(showEdit.image.split('/').pop() || 'banner');
          const webpFile = await convertImageToWebp(new File([imageBlob], fileName, { type: imageBlob.type || "image/jpeg" }));
          formData.append("ImageFile", webpFile, webpFile.name || fileName);
        } catch (imgError) {
          console.error("❌ Error fetching existing image:", imgError);
          // Create a minimal placeholder if fetch fails
          const placeholderBlob = new Blob([''], { type: 'image/webp' });
          formData.append("ImageFile", placeholderBlob, 'placeholder.webp');
        }
      }

      console.log("📤 Payload for re-adding:", {
        Id: bannerId,
        TenantId: tenantId,
        Name: showEdit.name.trim(),
        Active: showEdit.active,
        HasNewImage: !!showEdit.imageFile,
      });

      const res = await fetch(`${baseUrl}/Banners/AddTenantBanner?TenantId=${tenantId}`, {
        method: "POST",
        headers: {
          "Accept": "*/*",
          "Authorization": token ? `Bearer ${token}` : "",
        },
        body: formData,
      });

      let result = {};
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        try {
          result = await res.json();
        } catch (parseError) {
          const text = await res.text();
          console.error("Failed to parse JSON response:", parseError);
          result = { message: text || `HTTP ${res.status}: ${res.statusText}` };
        }
      } else {
        const text = await res.text();
        if (text) {
          try {
            result = JSON.parse(text);
          } catch {
            result = { message: text || `HTTP ${res.status}: ${res.statusText}` };
          }
        }
      }

      console.log("📥 AddTenantBanner (re-add) response status:", res.status, res.statusText);
      console.log("📥 AddTenantBanner (re-add) response data:", result);

      if (!res.ok) {
        const errorMessage = result?.message || result?.error || result?.Message || "Failed to update banner";
        throw new Error(errorMessage);
      }

      console.log("✅ Step 2 complete: Banner re-added with same ID");

      // Refresh banners list
      await fetchBanners();

      setShowEdit(null);

      // Show success message after everything is done
      Swal.fire({
        icon: "success",
        title: isArabic ? "تم التحديث! ✅" : "Updated! ✅",
        text: result?.message || (isArabic ? "تم تحديث اللافتة بنجاح" : "Banner updated successfully"),
        timer: 1500,
        showConfirmButton: false,
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
    } catch (err) {
      console.error("❌ Error updating banner:", err);
      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل التحديث ❌" : "Update Failed ❌",
        text: err.message || (isArabic ? "فشل تحديث اللافتة" : "Failed to update banner"),
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!perms.deleteBanners) {
      return;
    }
    if (!id || id <= 0) {
      Swal.fire({
        icon: "error",
        text: isArabic ? "معرف اللافتة غير صحيح" : "Invalid banner ID",
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
      return;
    }

    try {
      const token = localStorage.getItem("authToken");


      console.log("🗑️ Deleting banner:", { id, tenantId });

      const res = await fetch(`${baseUrl}/Banners/DeleteBanner?id=${id}&tenantId=${tenantId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
      });

      let result = {};
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        try {
          result = await res.json();
        } catch (parseError) {
          const text = await res.text();
          console.error("Failed to parse JSON response:", parseError);
          result = { message: text || `HTTP ${res.status}: ${res.statusText}` };
        }
      } else {
        const text = await res.text();
        if (text) {
          try {
            result = JSON.parse(text);
          } catch {
            result = { message: text || `HTTP ${res.status}: ${res.statusText}` };
          }
        }
      }

      console.log("📥 DeleteBanner response status:", res.status, res.statusText);
      console.log("📥 DeleteBanner response data:", result);

      if (!res.ok) {
        const errorMessage = result?.message || result?.error || result?.Message || "Failed to delete banner";
        throw new Error(errorMessage);
      }

      Swal.fire({
        icon: "success",
        title: isArabic ? "تم الحذف! ✅" : "Deleted! ✅",
        text: result?.message || (isArabic ? "تم حذف اللافتة بنجاح" : "Banner deleted successfully"),
        timer: 1500,
        showConfirmButton: false,
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });

      setShowDelete(null);
      fetchBanners();
    } catch (err) {
      console.error("❌ Error deleting banner:", err);
      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل الحذف ❌" : "Delete Failed ❌",
        text: err.message || (isArabic ? "فشل حذف اللافتة" : "Failed to delete banner"),
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
    }
  };

  const handleImageChange = async (e, setBanner) => {
    const file = e.target.files[0];
    if (file) {
      const webpFile = await convertImageToWebp(file);
      const url = URL.createObjectURL(webpFile);
      setBanner((prev) => ({ ...prev, image: url, imageFile: webpFile }));
    }
  };

  if (!perms.viewBanners) {
    return <NoAccess />;
  }

  return (
    <PageContainer title={isArabic ? "اللافتات" : "Banners"}>
      {/* ====== أدوات التحكم ====== */}


      {/* ====== جدول اللافتات ====== */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#7400BF]"></div>
            <p className="mt-4" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>
              {isArabic ? "جاري التحميل..." : "Loading..."}
            </p>
          </div>
        </div>
      ) : (
        <ResponsiveTable
          columns={[
            { header: isArabic ? "اسم اللافتة" : "Name", sortKey: "name" },
            { header: isArabic ? "الصورة" : "Image" },
            { header: isArabic ? "الحالة" : "Status", sortKey: "active" },
            { header: isArabic ? "تحكم" : "Actions" },
          ]}
          data={banners}
          searchable
          sortable
          filterOptions={{
            field: "active",
            options: [
              { value: "", label: isArabic ? "الكل" : "All" },
              { value: "true", label: isArabic ? "فعال" : "Active" },
              { value: "false", label: isArabic ? "غير\u00A0فعال" : "Inactive" },
            ],
          }}

          /* 👇 هذا المهم */
          headerActions={
            perms.addBanners && (
              <button
                onClick={() => setShowAdd(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold shadow "
                style={{ background: colors.mainColor }}
              >
                <FiPlus size={16} />
                {isArabic ? "إضافة لافتة" : "Add Banner"}
              </button>
            )
          }

          renderRow={(b) => (
            <>
              <td
                className="py-3 px-4 align-middle"
                style={{ textAlign: isArabic ? "right" : "left" }}
              >              {b.name}
              </td>
              <td
                className="py-3 px-4 align-middle"
                style={{ textAlign: isArabic ? "right" : "left" }}
              >              {b.image ? (
                <img
                  src={b.image}
                  alt={b.name}
                  className="w-16 h-16 object-cover rounded-lg border"
                  style={{ borderColor: isDark ? "rgba(255,255,255,0.1)" : "#e5e7eb" }}
                  onError={(e) => {
                    e.currentTarget.onerror = null; // ⛔ يمنع التكرار
                    e.currentTarget.src = "/placeholder-banner.png";
                  }}
                />

              ) : (
                <span className="opacity-60" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
                  {isArabic ? "لا توجد صورة" : "No image"}
                </span>
              )}
              </td>
              <td
                className="py-3 px-4 align-middle"
                style={{ textAlign: isArabic ? "right" : "left" }}
              >              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${b.active
                  ? "bg-green-500/10 text-green-400"
                  : "bg-red-500/10 text-red-400"
                  }`}
              >
                  {b.active
                    ? isArabic
                      ? "فعال"
                      : "Active"
                    : isArabic
                      ? "غير فعال"
                      : "Inactive"}
                </span>
              </td>
              <td
                className="py-3 px-4 align-middle"
                style={{ textAlign: isArabic ? "right" : "left" }}
              >              <div className="flex gap-2 items-center justify-start flex-wrap">
                  <button
                    onClick={() => setShowView(b)}
                    className="p-2 rounded-lg text-white"
                    style={{ background: colors.green }}
                  >
                    <FiEye />
                  </button>
                  {perms.editBanners && (
                    <button
                      onClick={() => setShowEdit(b)}
                      className="p-2 rounded-lg text-white"
                      style={{ background: colors.gold, color: isDark ? "#1e293b" : "#ffffff" }}
                    >
                      <FiEdit2 />
                    </button>
                  )}
                  {perms.deleteBanners && (
                    <button
                      onClick={() => setShowDelete(b)}
                      className="p-2 rounded-lg text-white"
                      style={{ background: colors.red }}
                    >
                      <FiTrash2 />
                    </button>
                  )}
                </div>
              </td>
            </>
          )}
          renderCard={(b) => (
            <div className="space-y-3">
              <div className="flex gap-4 items-start">
                {b.image && (
                  <img
                    src={b.image}
                    alt={b.name}
                    className="w-16 h-16 object-cover rounded-lg flex-shrink-0 border"
                    style={{ borderColor: isDark ? "rgba(255,255,255,0.1)" : "#e5e7eb" }}
                  />
                )}
                <div className="flex-1">
                  <h3 className="font-bold text-lg" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                    {b.name}
                  </h3>
                  <span
                    className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold ${b.active
                      ? "bg-green-500/10 text-green-400"
                      : "bg-red-500/10 text-red-400"
                      }`}
                  >
                    {b.active
                      ? isArabic
                        ? "فعال"
                        : "Active"
                      : isArabic
                        ? "غير فعال"
                        : "Inactive"}
                  </span>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowView(b)}
                  className="flex-1 p-2 rounded-lg text-white text-sm"
                  style={{ background: colors.green }}
                >
                  <FiEye className="inline mr-1" />
                  {isArabic ? "عرض" : "View"}
                </button>
                <button
                  onClick={() => setShowEdit(b)}
                  className="flex-1 p-2 rounded-lg text-white text-sm"
                  style={{ background: colors.gold, color: isDark ? "#1e293b" : "#ffffff" }}
                >
                  <FiEdit2 className="inline mr-1" />
                  {isArabic ? "تعديل" : "Edit"}
                </button>
                <button
                  onClick={() => setShowDelete(b)}
                  className="flex-1 p-2 rounded-lg text-white text-sm"
                  style={{ background: colors.red }}
                >
                  <FiTrash2 className="inline mr-1" />
                  {isArabic ? "حذف" : "Delete"}
                </button>
              </div>
            </div>
          )}
        />
      )}

      {/* ===== مودالات ===== */}
      <AnimatePresence>
        {showAdd && (
          <BannerModal
            isArabic={isArabic}
            isDark={isDark}
            colors={colors}
            title={isArabic ? "إضافة لافتة جديدة" : "Add New Banner"}
            banner={newBanner}
            setBanner={setNewBanner}
            onSave={handleAdd}
            onClose={() => setShowAdd(false)}
            handleImageChange={handleImageChange}
            saving={saving}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showEdit && (
          <BannerModal
            isArabic={isArabic}
            isDark={isDark}
            colors={colors}
            title={isArabic ? "تعديل اللافتة" : "Edit Banner"}
            banner={{ ...showEdit, imageFile: showEdit.imageFile || null }}
            setBanner={setShowEdit}
            onSave={handleEditSave}
            onClose={() => setShowEdit(null)}
            handleImageChange={handleImageChange}
            saving={saving}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showView && (
          <ViewBannerModal
            isArabic={isArabic}
            isDark={isDark}
            colors={colors}
            banner={showView}
            onClose={() => setShowView(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDelete && (
          <ConfirmDeleteModal
            isArabic={isArabic}
            isDark={isDark}
            colors={colors}
            name={showDelete.name}
            onConfirm={() => handleDelete(showDelete.id)}
            onClose={() => setShowDelete(null)}
          />
        )}
      </AnimatePresence>
    </PageContainer>
  );
}

/* ===== مودال الإضافة / التعديل ===== */
function BannerModal({ isArabic, isDark, colors, title, banner, setBanner, onSave, onClose, handleImageChange, saving }) {
  const { colors: uiColors } = useUI();
  const finalColors = colors || uiColors;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[9999]"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative w-[95%] sm:w-[90%] max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl p-6 ${isDark ? "bg-[#1E293B] text-gray-100" : "bg-white text-gray-900"
          }`}
      >
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-xl font-bold" style={{ color: finalColors.mainColor }}>
            {title}
          </h2>
          <button onClick={onClose} className="text-gray-400 transition">
            <FiX size={22} />
          </button>
        </div>

        <label className="block mb-2 font-semibold" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
          {isArabic ? "اسم اللافتة" : "Banner Name"}
        </label>
        <input
          type="text"
          value={banner.name}
          onChange={(e) => setBanner({ ...banner, name: e.target.value })}
          className="w-full rounded-lg border px-3 py-2 mb-4 focus:outline-none transition-all"
          style={{
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#ffffff",
            borderColor: isDark ? "rgba(255, 255, 255, 0.2)" : "#d1d5db",
            color: isDark ? "#f1f5f9" : "#111827",
          }}
          onFocus={(e) => {
            e.target.style.borderColor = finalColors.mainColor;
            e.target.style.boxShadow = `0 0 0 2px ${finalColors.mainColor}20`;
          }}
          onBlur={(e) => {
            e.target.style.borderColor = isDark ? "rgba(255, 255, 255, 0.2)" : "#d1d5db";
            e.target.style.boxShadow = "none";
          }}
        />

        {/* صورة اللافتة */}
        <label className="block mb-3 font-semibold" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
          {isArabic ? "صورة اللافتة" : "Banner Image"}
        </label>
        <div
          className="border-2 border-dashed rounded-xl p-6 text-center mb-5 transition-all "
          style={{
            borderColor: banner.image ? (isDark ? "rgba(255, 255, 255, 0.2)" : "#d1d5db") : (isDark ? "rgba(116, 0, 191, 0.4)" : "#e9d5ff"),
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "#f9fafb"
          }}
        >
          {banner.image ? (
            <div className="relative">
              <img
                src={banner.image}
                alt="preview"
                className="w-full h-56 object-cover rounded-lg mb-4 shadow-lg"
              />
              <button
                onClick={() => setBanner({ ...banner, image: "", imageFile: null })}
                className="absolute top-2 right-2 p-2 rounded-full text-white shadow-lg transition-transform"
                style={{ background: "rgba(239, 68, 68, 0.9)" }}
                title={isArabic ? "إزالة الصورة" : "Remove Image"}
              >
                <FiX size={18} />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 opacity-70">
              <div className="mb-4 p-4 rounded-full" style={{ background: isDark ? "rgba(116, 0, 191, 0.2)" : "rgba(116, 0, 191, 0.1)" }}>
                <FiImage size={48} style={{ color: finalColors.mainColor }} />
              </div>
              <p className="mb-2 text-lg font-medium" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                {isArabic ? "لم يتم اختيار صورة" : "No image selected"}
              </p>
              <p className="text-sm" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
                {isArabic ? "اسحب وأفلت الصورة هنا أو اختر ملف" : "Drag and drop image here or choose file"}
              </p>
            </div>
          )}
          <label
            className="inline-block cursor-pointer"
            style={{ position: "relative" }}
          >
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageChange(e, setBanner)}
              style={{
                position: "absolute",
                width: 0,
                height: 0,
                opacity: 0,
                visibility: "hidden",
                pointerEvents: "none",
                overflow: "hidden",
                clip: "rect(0, 0, 0, 0)",
                border: 0,
                margin: 0,
                padding: 0,
                left: "-9999px"
              }}
            />
            <div
              className="inline-flex items-center gap-2 px-6 py-3 text-white rounded-lg font-semibold shadow-md transition-all cursor-pointer"
              style={{ background: finalColors.mainColor }}
            >
              <FiImage size={18} />
              <span>{banner.image ? (isArabic ? "تغيير الصورة" : "Change Image") : (isArabic ? "رفع صورة" : "Upload Image")}</span>
            </div>
          </label>
        </div>

        {/* حالة النشاط */}
        <div className="mb-5" dir={isArabic ? "rtl" : "ltr"}>
          <label className="block mb-3 font-semibold" style={{ color: isDark ? "#f1f5f9" : "#111827", textAlign: isArabic ? "right" : "left" }}>
            {isArabic ? "حالة النشاط" : "Status"}
          </label>
          <div
            className="flex items-center"
            style={{
              justifyContent: "flex-start",
              direction: isArabic ? "rtl" : "ltr"
            }}
          >
            <label
              className="flex items-center gap-3 cursor-pointer group"
              style={{
                flexDirection: isArabic ? "row" : "row",
                direction: isArabic ? "rtl" : "ltr"
              }}
            >
              {/* Toggle Switch - Same design as Categories */}
              <button
                onClick={() => setBanner({ ...banner, active: !banner.active })}
                className="relative w-12 h-6 rounded-full transition-colors cursor-pointer"
                style={{
                  backgroundColor: banner.active ? finalColors.mainColor : (isDark ? "#475569" : "#d1d5db")
                }}
              >
                <span
                  className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${banner.active ? "translate-x-6" : "translate-x-0"
                    }`}
                />
              </button>

              {/* Status Text with Badge Style */}
              <div
                className={`
                  px-4 py-1.5 rounded-lg font-semibold text-sm transition-all duration-300
                  ${banner.active
                    ? 'bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20'
                    : 'bg-gray-500/10 text-gray-600 dark:text-gray-400 border border-gray-500/20'
                  }
                `}
                style={{
                  backgroundColor: banner.active
                    ? 'rgba(16, 185, 129, 0.1)'
                    : (isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(107, 114, 128, 0.1)'),
                  color: banner.active
                    ? '#059669'
                    : (isDark ? '#94a3b8' : '#4b5563'),
                  borderColor: banner.active
                    ? 'rgba(16, 185, 129, 0.2)'
                    : (isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(107, 114, 128, 0.2)'),
                }}
              >
                {banner.active
                  ? (isArabic ? "✓ فعال" : "✓ Active")
                  : (isArabic ? "✗ غير فعال" : "✗ Inactive")
                }
              </div>
            </label>
          </div>
        </div>

        <button
          onClick={onSave}
          disabled={saving}
          className="w-full py-2 rounded-lg text-white font-semibold shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: saving ? "#94a3b8" : finalColors.mainColor }}
        >
          {saving ? (
            <>
              <div className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              {isArabic ? "جاري الحفظ..." : "Saving..."}
            </>
          ) : (
            isArabic ? "حفظ" : "Save"
          )}
        </button>
      </div>
    </motion.div>
  );
}

/* ===== مودال العرض ===== */
function ViewBannerModal({ isArabic, isDark, colors, banner, onClose }) {
  const { colors: uiColors } = useUI();
  const finalColors = colors || uiColors;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[9999]"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative w-[95%] sm:w-[90%] max-w-lg rounded-2xl shadow-xl p-6 ${isDark ? "bg-[#1E293B] text-gray-100" : "bg-white text-gray-900"
          }`}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 transition"
        >
          <FiX size={22} />
        </button>
        <h2 className="text-xl font-bold mb-4 text-center" style={{ color: finalColors.mainColor }}>
          {banner.name}
        </h2>
        <img
          src={banner.image}
          alt={banner.name}
          className="w-full h-64 object-cover rounded-lg border border-gray-600 mb-4"
        />
        <p className="text-center font-semibold">
          {banner.active
            ? isArabic
              ? "الحالة: فعّال"
              : "Status: Active"
            : isArabic
              ? "الحالة: غير فعّال"
              : "Status: Inactive"}
        </p>
      </div>
    </motion.div>
  );
}

/* ===== مودال الحذف ===== */
function ConfirmDeleteModal({ isArabic, isDark, colors, name, onConfirm, onClose }) {
  const { colors: uiColors } = useUI();
  const finalColors = colors || uiColors;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[9999]"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-[95%] sm:w-[90%] max-w-md rounded-2xl shadow-xl p-6 ${isDark ? "bg-[#1E293B] text-gray-100" : "bg-white text-gray-900"
          }`}
      >
        <h2 className="text-lg font-bold mb-3 text-center" style={{ color: finalColors.red }}>
          {isArabic ? "تأكيد الحذف" : "Confirm Deletion"}
        </h2>
        <p className="text-center mb-5">
          {isArabic
            ? `هل أنت متأكد أنك تريد حذف اللافتة "${name}"؟`
            : `Are you sure you want to delete banner "${name}"?`}
        </p>
        <div className="flex justify-center gap-3">
          <button
            onClick={onConfirm}
            className="px-5 py-2 rounded-lg text-white font-semibold shadow-md"
            style={{ background: colors.red }}
          >
            {isArabic ? "نعم، احذف" : "Yes, Delete"}
          </button>
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg font-semibold border border-gray-500/30"
          >
            {isArabic ? "إلغاء" : "Cancel"}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
